import React, { useMemo, useState } from "react";
import { CrewPilotModuleShell } from "../../CrewPilotModuleShell";
import {
  buildInvoiceReportModel,
  exportInvoiceReportPdf,
} from "../../../helpers/invoiceReport";
import { InvoiceReportDocument } from "./InvoiceReportDocument";
import {
  REPORT_PREVIEW_HELP_TEXT,
  ReportPreviewChrome,
  ReportPreviewFrame,
} from "../../report/ReportPreviewChrome";
import { useOrganization } from "../../../context/OrganizationContext";
import { resolveOrgBranding } from "../../../helpers/resolveOrgBranding";
import { getOrganizationPaymentInstructions } from "../../../helpers/invoicePaymentInstructions";
import { getOrganizationPaymentTermsConfig } from "../../../helpers/invoicePaymentTerms";
import { getOrganizationCustomerNotes } from "../../../helpers/invoiceCustomerNotes";
import { getOrganizationInvoiceFooter } from "../../../helpers/invoiceFooter";
import "./InvoiceReportPreviewScreen.css";

export function InvoiceReportPreviewScreen({
  invoice,
  options,
  onBack,
  onEditOptions,
}) {
  const [pdfExporting, setPdfExporting] = useState(false);
  const { settings } = useOrganization();
  const branding = useMemo(() => resolveOrgBranding(settings), [settings]);
  const organizationPaymentInstructions = useMemo(
    () => getOrganizationPaymentInstructions(settings),
    [settings],
  );
  const organizationPaymentTerms = useMemo(
    () => getOrganizationPaymentTermsConfig(settings),
    [settings],
  );
  const organizationCustomerNotes = useMemo(
    () => getOrganizationCustomerNotes(settings),
    [settings],
  );
  const organizationInvoiceFooter = useMemo(
    () => getOrganizationInvoiceFooter(settings),
    [settings],
  );

  const reportModel = useMemo(
    () =>
      buildInvoiceReportModel(invoice, {
        ...options,
        organizationPaymentInstructions,
        organizationPaymentTerms,
        organizationCustomerNotes,
        organizationInvoiceFooter,
      }),
    [
      invoice,
      options,
      organizationPaymentInstructions,
      organizationPaymentTerms,
      organizationCustomerNotes,
      organizationInvoiceFooter,
    ],
  );

  async function handleExportPdf() {
    try {
      setPdfExporting(true);
      await exportInvoiceReportPdf(reportModel, { branding });
    } catch (error) {
      console.error("Invoice PDF export failed:", error);
      window.alert("Could not export the PDF. Please try again.");
    } finally {
      setPdfExporting(false);
    }
  }

  const toolbarActions = (
    <>
      {onEditOptions && (
        <button type="button" className="nx-btn" onClick={onEditOptions}>
          Edit Options
        </button>
      )}
      <button
        type="button"
        className="nx-btn nx-btn-gold"
        onClick={handleExportPdf}
        disabled={pdfExporting}
      >
        {pdfExporting ? "Exporting PDF…" : "Export PDF"}
      </button>
    </>
  );

  const documentLabel =
    invoice?.invoice_number || invoice?.title || "Invoice document";

  return (
    <CrewPilotModuleShell
      title="Invoice Preview"
      subtitle={invoice?.invoice_number || invoice?.title || ""}
      sectionLabel="Invoice"
      onBack={onBack}
    >
      <div className="invoice-preview">
        <div className="invoice-preview__toolbar">
          <div className="invoice-preview__toolbar-label">{documentLabel}</div>
          <div className="invoice-preview__toolbar-actions">{toolbarActions}</div>
        </div>

        <ReportPreviewFrame className="invoice-preview__frame">
          <InvoiceReportDocument reportModel={reportModel} />
        </ReportPreviewFrame>

        <div className="invoice-preview__mobile-chrome">
          <ReportPreviewChrome helpText={REPORT_PREVIEW_HELP_TEXT}>
            {toolbarActions}
          </ReportPreviewChrome>
        </div>
      </div>
    </CrewPilotModuleShell>
  );
}
